import React, { useState, useContext, useRef } from "react";

import { Typography, Stack, Box } from "@mui/material";

import { SettingsOptionsContext } from "../SettingsOptions";
import PopoverColorPicker from "components/uiElements/inputs/colorSelector/parts/PopoverColorPicker";
import ColorSwatch from "components/uiElements/inputs/colorSelector/parts/ColorSwatch";

import { AnyColor } from "react-colorful/dist/types";

interface SettingsColorPickerProps {
  color: string | AnyColor | undefined;
  /**
   * Used for components that must have a color applied. If the selected color is reset,
   * it will rever to this color.
   */
  defaultColor?: string;
  /**
   * If true, the alpha value selector will be hidden, only allowing opaque color selection.
   */
  disableAlpha?: boolean;
  /**
   * Acts as the identifier of the settings option.
   */
  keyid: string;
  /**
   * Label to be displayed in the UI
   */
  label: string;
}

export default function SettingsColorPicker(props: SettingsColorPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const settingsOptions = useContext(SettingsOptionsContext);

  const [selectedColor, setSelectedColor] = useState<AnyColor | undefined>(props.color);
  const [isSelectedColorOpen, setIsSelectedColorOpen] = useState(false);

  const handleOpenColor = () => {
    if (!isSelectedColorOpen) setIsSelectedColorOpen(true);
  };

  const handleColorChange = (newColor: AnyColor) => {
    settingsOptions?.updateSettingsState(props.keyid, newColor);
    setSelectedColor(newColor);
  };

  const handleCloseColor = () => setIsSelectedColorOpen(false);

  return (
    <Stack direction="row" sx={{
      alignItems: "center"
    }}>
      <Typography sx={{ flex: 1 }}>{props.label}</Typography>
      <Box sx={{ flex: 2 }}>
        {containerRef.current &&
          <PopoverColorPicker
            anchor={containerRef.current}
            color={selectedColor}
            defaultColor={props.defaultColor}
            disableAlpha={props.disableAlpha}
            isOpen={isSelectedColorOpen}
            onChange={handleColorChange}
            onClose={handleCloseColor}
            showInput={true} />}
        <Box ref={containerRef}>
          <ColorSwatch
            color={props.color}
            defaultColor={props.defaultColor}
            onClick={handleOpenColor} />
        </Box>
      </Box>
    </Stack>
  );
}